import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  TextField,
  Divider,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InfoOutlined, ContentCopy, AddCircleOutline } from '@mui/icons-material';
import { useSectionStyles } from '@/styles/utilities/hooks';
import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';

const FMOClusterConfiguration = ({
  loading,
  selectedRows,
  fileAnalysis,
  clusterspecs,
  clustersWithTargets,
  clusterSelectionMode,
  selectedClusterspec,
  selectedCluster,
  clusterName,
  configurationQueue,
  activeConfigId,
  onSelectionModeChange,
  onClusterspecSelect,
  onClusterSelect,
  onClusterNameChange,
  onInspectCluster,
  clusterDialogOpen,
  onDialogUpdate,
  onOpenResidueEditor,
  onCreateNow
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  // Filter clusters by target
  const availableClusters = useMemo(() => {
    if (!fileAnalysis.isSingleTarget) return [];
    
    const target = fileAnalysis.targets[0];
    
    // Make comparison more robust - handle case and whitespace differences
    const filtered = clustersWithTargets.filter(cluster => {
      // Skip clusters without valid targets
      if (!cluster.target || cluster.target === '<none>' || cluster.target === '<unknown>') {
        return false;
      }
      // Compare with trimmed, lowercase values for more robust matching
      return cluster.target.trim().toLowerCase() === target.trim().toLowerCase();
    });
    
    return filtered;
  }, [clustersWithTargets, fileAnalysis]);

  const handleClusterspecSelect = (specId) => {
    if (!loading) {
      // Check if there's an existing configuration with this clusterspec
      const existingConfig = configurationQueue.find(config => 
        config.clusterSelectionMode === 'clusterspec' && 
        config.selectedClusterspec === specId &&
        config.clusterName // Has a custom name
      );
      
      // Use existing config's name if found, otherwise reset to null
      const newClusterName = existingConfig?.clusterName || null;
      
      onClusterspecSelect(specId);
      onSelectionModeChange('clusterspec');
      onClusterSelect('');
      onClusterNameChange(newClusterName);
    }
  };

  const handleClusterSelect = async (cluster) => {
    if (!loading && selectedRows.length > 0 && fileAnalysis.isSingleTarget) {
      onClusterSelect(cluster.id_);
      onSelectionModeChange('existing');
      onClusterspecSelect('');
      onClusterNameChange(null); // Reset cluster name when switching to existing
      
      // If dialog is open, update it with new cluster details
      if (clusterDialogOpen && onDialogUpdate) {
        try {
          const response = await api.get(API_ENDPOINTS.CLUSTERS.BY_ID(cluster.id_));
          onDialogUpdate(response.data, fileAnalysis.targets[0]);
        } catch (error) {
          console.error('Failed to fetch cluster details:', error);
        }
      }
    }
  };

  const generateDefaultClusterName = () => {
    // Check if there's an existing configuration with the same clusterspec
    const existingConfig = configurationQueue.find(config => 
      config.clusterSelectionMode === 'clusterspec' && 
      config.selectedClusterspec === selectedClusterspec &&
      config.clusterName // Has a custom name
    );
    
    // If found, use its custom name as default
    if (existingConfig && existingConfig.clusterName) {
      return existingConfig.clusterName;
    }
    
    // Otherwise, generate default name
    const today = new Date().toISOString().slice(2, 10).replace(/-/g, '').slice(0, 6);
    const clusterspecName = clusterspecs.find(spec => spec.id_ === selectedClusterspec)?.name || '';
    
    if (fileAnalysis.targets.length > 0) {
      const firstTarget = fileAnalysis.targets[0];
      const targetRows = selectedRows.filter(row => row.target === firstTarget);
      const batches = [...new Set(
        targetRows
          .map(row => row.batch)
          .filter(Boolean)
          .filter(b => b !== '<none>')
      )];
      
      const batchPart = batches.length === 1 && batches[0] 
        ? batches[0] 
        : batches.length > 1 
          ? 'mixed' 
          : `${targetRows.length}files`;
      
      const baseName = `${today}_${firstTarget}_${batchPart}_${clusterspecName}`;
      
      if (fileAnalysis.targets.length > 1) {
        return `${baseName}, ...`;
      } else {
        return baseName;
      }
    } else {
      return `${today}_${clusterspecName}`;
    }
  };

  return (
    <Box sx={sectionStyles.sectionContainer}>
      {/* RESIDUE SELECTION Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
        <Typography variant="caption" sx={sectionStyles.sectionHeader}>
          RESIDUE SELECTION
        </Typography>
        
        {/* Two Row Layout */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
        {/* First Row: Select from Template Cards */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" sx={sectionStyles.subsectionHeader}>
            Create New with Cutoff Rule
          </Typography>
          <Box sx={{
            height: theme.spacing(24),
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: theme.spacing(0.5),
            overflow: 'auto',
            backgroundColor: theme.palette.background.paper,
            p: theme.spacing(0.5)
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
              {clusterspecs
                .sort((a, b) => (a.cutoff || 0) - (b.cutoff || 0))
                .map(spec => (
                  <Card 
                    key={spec.id_}
                    elevation={0}
                    sx={{ 
                      border: '1px solid',
                      borderColor: (clusterSelectionMode === 'clusterspec' && selectedClusterspec === spec.id_) 
                        ? theme.palette.selection.border 
                        : theme.palette.divider,
                      backgroundColor: (clusterSelectionMode === 'clusterspec' && selectedClusterspec === spec.id_)
                        ? theme.palette.selection.background
                        : theme.palette.background.paper,
                      opacity: loading ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleClusterspecSelect(spec.id_)}
                      disabled={loading}
                      sx={{ p: 0 }}
                    >
                      <CardContent sx={{ p: theme.spacing(0.5), '&:last-child': { pb: theme.spacing(0.5) } }}>
                        <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, lineHeight: 1.2 }}>
                          {spec.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.xs, color: theme.palette.text.secondary, lineHeight: 1.2, display: 'block' }}>
                          {spec.description || `Includes residues within ${spec.cutoff || 'default'} Å`}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
            </Box>
          </Box>

          {/* Residue Set Name Field - for Create New */}
          <Box sx={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center', mt: theme.spacing(1) }}>
            <TextField
              size="small"
              label="Residue Set Name"
              value={clusterSelectionMode === 'clusterspec' && selectedClusterspec
                ? (clusterName !== null ? clusterName : generateDefaultClusterName())
                : ''}
              onChange={(e) => onClusterNameChange(e.target.value)}
              onFocus={(e) => {
                if (clusterName === null && clusterSelectionMode === 'clusterspec' && selectedClusterspec) {
                  onClusterNameChange(e.target.value);
                }
              }}
              disabled={(() => {
                // Disable when not in clusterspec mode or no clusterspec selected
                if (clusterSelectionMode !== 'clusterspec' || !selectedClusterspec) return true;

                // Disable for multiple targets
                if (!fileAnalysis.isSingleTarget) return true;

                // Check if there's already a configuration with this clusterspec (excluding the active one)
                const hasDuplicateClusterspec = configurationQueue.some(config =>
                  config.id !== activeConfigId && // Not the current active config
                  config.clusterSelectionMode === 'clusterspec' &&
                  config.selectedClusterspec === selectedClusterspec
                );

                return hasDuplicateClusterspec;
              })()}
              placeholder="Enter name"
              sx={{
                flex: 1,
                '& .MuiInputBase-input': { fontSize: theme.typography.fontSize.base }
              }}
            />
            <Button
              size="small"
              startIcon={<AddCircleOutline sx={{ fontSize: theme.dimensions.icon.small }} />}
              onClick={onCreateNow}
              disabled={loading || clusterSelectionMode !== 'clusterspec' || !selectedClusterspec ||
                !(clusterName !== null ? clusterName : generateDefaultClusterName()).trim()}
              sx={{
                minWidth: 'auto',
                px: theme.spacing(1),
                py: 0,
                height: 'auto',
                fontSize: theme.typography.fontSize.xs,
                textTransform: 'none',
                fontWeight: theme.typography.fontWeight.medium,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                '& .MuiButton-startIcon': {
                  marginRight: theme.spacing(0.5),
                  marginLeft: 0
                }
              }}
            >
              Create Now
            </Button>
          </Box>
        </Box>

        {/* Visual separator with "or" text */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          px: theme.spacing(2)
        }}>
          <Divider sx={{ flex: 1, opacity: 0.4 }} />
          <Typography
            variant="caption"
            sx={{
              mx: theme.spacing(1),
              color: theme.palette.text.disabled,
              fontSize: theme.typography.fontSize.xs,
              fontStyle: 'italic'
            }}
          >
            or
          </Typography>
          <Divider sx={{ flex: 1, opacity: 0.4 }} />
        </Box>

        {/* Second Row: Use Existing Cluster Cards */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: theme.spacing(24) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={sectionStyles.subsectionHeader}>
              Reuse Existing Residue Set
            </Typography>
            <Button
              size="small"
              startIcon={<ContentCopy sx={{ fontSize: theme.dimensions.icon.small }} />}
              onClick={onOpenResidueEditor}
              disabled={loading || selectedRows.length === 0 || clusterSelectionMode !== 'existing' || !selectedCluster}
              sx={{
                minWidth: 'auto',
                px: theme.spacing(1),
                py: 0,
                height: 'auto',
                fontSize: theme.typography.fontSize.xs,
                textTransform: 'none',
                fontWeight: theme.typography.fontWeight.medium,
                lineHeight: 1,
                '& .MuiButton-startIcon': {
                  marginRight: theme.spacing(0.5),
                  marginLeft: 0
                }
              }}
            >
              Create Modified Copy
            </Button>
          </Box>
          <Box sx={{ 
            border: '1px solid', 
            borderColor: theme.palette.divider, 
            borderRadius: theme.spacing(0.5), 
            flex: '1 1 0',
            minHeight: 0,
            overflow: 'auto',
            backgroundColor: theme.palette.background.paper,
            p: theme.spacing(0.5),
            position: 'relative'
          }}>
            {availableClusters.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: 160,
                color: theme.palette.text.secondary
              }}>
                <Typography variant="caption" sx={{ fontSize: theme.typography.fontSize.xs, textAlign: 'center' }}>
                  {loading ? 'Loading residue sets...' : 
                   selectedRows.length === 0 ? 'Select files first' :
                   !fileAnalysis.isSingleTarget ? 'Multiple targets selected' :
                   'No existing residue sets for this target'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                {availableClusters
                  .sort((a, b) => (a.name || `Cluster ${a.id_}`).localeCompare(b.name || `Cluster ${b.id_}`))
                  .map(cluster => (
                  <Card 
                    key={cluster.id_}
                    elevation={0}
                    sx={{ 
                      border: '1px solid',
                      borderColor: (clusterSelectionMode === 'existing' && selectedCluster === cluster.id_) 
                        ? theme.palette.selection.border 
                        : theme.palette.divider,
                      backgroundColor: (clusterSelectionMode === 'existing' && selectedCluster === cluster.id_)
                        ? theme.palette.selection.background
                        : theme.palette.background.paper,
                      opacity: (loading || selectedRows.length === 0 || !fileAnalysis.isSingleTarget) ? 0.5 : 1,
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleClusterSelect(cluster)}
                      disabled={loading || selectedRows.length === 0 || !fileAnalysis.isSingleTarget}
                      sx={{ p: 0 }}
                    >
                      <CardContent sx={{ 
                        p: theme.spacing(0.5), 
                        pr: theme.spacing(0.5),
                        '&:last-child': { pb: theme.spacing(0.5) },
                        position: 'relative'
                      }}>
                        <Box sx={{ 
                          pr: (clusterSelectionMode === 'existing' && selectedCluster === cluster.id_) ? theme.spacing(3.5) : 0
                        }}>
                          <Typography 
                            variant="caption" 
                            component="div"
                            title={cluster.name || `Cluster ${cluster.id_}`}
                            sx={{ 
                              fontSize: theme.typography.fontSize.sm, 
                              fontWeight: theme.typography.fontWeight.medium, 
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%'
                            }}
                          >
                            {cluster.name || `Cluster ${cluster.id_}`}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            component="div"
                            sx={{ 
                              fontSize: theme.typography.fontSize.xs, 
                              color: theme.palette.text.secondary, 
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {cluster.residueCount} residues • {cluster.memberCount} structures
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    {(clusterSelectionMode === 'existing' && selectedCluster === cluster.id_) && (
                      <IconButton
                        size="small"
                        onClick={(e) => onInspectCluster(cluster.id_, e)}
                        sx={{ 
                          position: 'absolute',
                          top: theme.spacing(0.25),
                          right: theme.spacing(0.25),
                          p: theme.spacing(0.25),
                          backgroundColor: theme.palette.selection.backgroundLight,
                          '&:hover': {
                            backgroundColor: theme.palette.selection.background
                          }
                        }}
                      >
                        <InfoOutlined sx={{ fontSize: theme.dimensions.icon.small }} />
                      </IconButton>
                    )}
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FMOClusterConfiguration;